import type {
	DeleteTripMutation,
	DeleteTripMutationVariables,
} from '$src/lib/queries/generated/DeleteTrip'
import type {
	DuplicateTripMutation,
	DuplicateTripMutationVariables,
	InsertTripMutation,
	InsertTripMutationVariables,
} from '$src/lib/queries/generated/InsertTrip'
import {
	TripsByFilterDocument,
	type TripsByFilterQuery,
	type TripsByFilterQueryVariables,
} from '$src/lib/queries/generated/QueryTrips'
import type { Cache } from '@urql/exchange-graphcache'
import { cloneDeep } from 'lodash-es'
import type {
	InsertMediaMutation,
	InsertMediaMutationVariables,
} from '$src/lib/queries/generated/InsertMedia'
import {
	MediaConnectionDocument,
	type MediaConnectionQuery,
	type MediaConnectionQueryVariables,
} from '$src/lib/queries/generated/QueryMedia'
import type {
	MakePurchaseMutation,
	MakePurchaseMutationVariables,
} from '$lib/queries/generated/MakePurchase'
import type {
	PublishListingMutation,
	PublishListingMutationVariables,
	UnpublishListingMutation,
	UnpublishListingMutationVariables,
} from '$lib/queries/generated/PublishListing'
import type { TripsFieldsFragment } from '$lib/queries/fragments/generated/TripsFields'
import {
	TripBaseDocument,
	TripDocument,
	type TripBaseQuery,
	type TripBaseQueryVariables,
	type TripQuery,
	type TripQueryVariables,
} from '$lib/queries/generated/QueryTrip'
import type {
	UpdateTripMutation,
	UpdateTripMutationVariables,
} from '$lib/queries/generated/UpdateTrip'
import { TripPaginationFilter } from '$src/lib/graphql/enums'

// Helper function to sort trip cache fields by filter
export function sortTripsCacheFields(options: { cache: Cache; filter?: TripPaginationFilter }) {
	const { cache, filter } = options

	// Get all fields from the cache
	const allFields = cache.inspectFields('Query')

	// Filter for tripConnection fields
	const tripFields = allFields.filter((field) => field.fieldName === 'tripConnection')

	// Filter by the specified filter if provided
	const filteredFields = tripFields.filter((field) => {
		// If filter is provided, only include fields with matching filter
		if (filter) {
			// Access filter through the where argument
			const fieldFilter = (field.arguments as any)?.where?.filter

			if (!fieldFilter) {
				return false
			}

			// Convert both to strings for comparison and logging
			const fieldFilterStr = String(fieldFilter)
			const filterStr = String(filter)

			// Compare the string representations
			return fieldFilterStr === filterStr
		}
		return true
	})

	const sortedFields = [...filteredFields].sort((a, b) => {
		const afterA = (a.arguments as TripsByFilterQueryVariables)?.after ?? ''
		const afterB = (b.arguments as TripsByFilterQueryVariables)?.after ?? ''
		return afterB.localeCompare(afterA)
	})

	const latestField = sortedFields[0]
	const latestCursor = latestField
		? (latestField.arguments as TripsByFilterQueryVariables)?.after
		: null

	return { sortedFields, latestField, latestCursor }
}

// Function to update the TripsByFilter query cache
export function updateTripsByFilterQueryCache(
	cache: Cache,
	tripId: string,
	updatedTrip: Partial<TripsFieldsFragment>,
	filter?: TripPaginationFilter,
) {
	const { sortedFields, latestField, latestCursor } = sortTripsCacheFields({ cache, filter })

	sortedFields.forEach((field) => {
		cache.updateQuery<TripsByFilterQuery, TripsByFilterQueryVariables>(
			{
				query: TripsByFilterDocument,
				variables: field.arguments as TripsByFilterQueryVariables,
			},
			(data) => {
				if (!data?.tripConnection?.edges) return data

				const edges = data.tripConnection.edges
				const existingEdgeIndex = edges.findIndex((edge) => edge.node?.id === tripId)

				if (existingEdgeIndex !== -1) {
					// Update existing edge
					const updatedEdges = [...edges]
					updatedEdges[existingEdgeIndex] = {
						...edges[existingEdgeIndex],
						node: {
							...edges[existingEdgeIndex].node,
							...updatedTrip,
						},
					}

					return {
						...data,
						tripConnection: {
							...data.tripConnection,
							edges: updatedEdges,
						},
					}
				}

				return data
			},
		)
	})
}

// Function to insert a trip into the TripsByFilter query cache
export function insertTripToFilterQueryCache(
	cache: Cache,
	newTrip: TripsFieldsFragment,
	filter: TripPaginationFilter,
) {
	const { sortedFields } = sortTripsCacheFields({ cache, filter })

	// If no fields found, create a new query in the cache
	if (sortedFields.length === 0) {
		// Create default variables for the new query with filter both at top level and inside where object
		const variables = { filter, where: { filter }, first: 10 }

		// Update or create the query in the cache
		cache.updateQuery<TripsByFilterQuery, TripsByFilterQueryVariables>(
			{
				query: TripsByFilterDocument,
				variables,
			},
			(data) => {
				// Create new data if none exists
				data = ensureTripsConnection(data)

				// Create a new edge for the trip
				const newEdge = {
					cursor: null,
					__typename: 'TripEdge' as const,
					node: newTrip,
				}

				// Add the new edge to the beginning
				data.tripConnection.edges = [newEdge]
				data.tripConnection.totalCount = 1

				return data
			},
		)
		return
	}

	// Only update the first page (usually the one without 'after' cursor)
	const firstPageField = sortedFields.find((field) => {
		const args = field.arguments as any
		return !args?.after && args?.where?.filter === String(filter)
	})

	if (!firstPageField) {
		// If no first page, use the first sorted field
		if (sortedFields.length > 0) {
			updateExistingQuery(cache, sortedFields[0], newTrip)
		}
		return
	}

	updateExistingQuery(cache, firstPageField, newTrip)
}

// Helper function to update an existing query
function updateExistingQuery(cache: Cache, field: any, newTrip: TripsFieldsFragment) {
	// Clone the original arguments to avoid modifying them
	const originalArgs = field.arguments as any

	// Create a new variables object with the filter flattened
	const variables = {
		...originalArgs,
		filter: originalArgs.where?.filter, // Extract filter from where
	}

	cache.updateQuery<TripsByFilterQuery, TripsByFilterQueryVariables>(
		{
			query: TripsByFilterDocument,
			variables: variables as TripsByFilterQueryVariables,
		},
		(data) => {
			data = ensureTripsConnection(data)
			if (data.tripConnection) {
				const newEdge = {
					cursor: null,
					__typename: 'TripEdge' as const,
					node: newTrip,
				}

				// Add the new edge to the beginning of the list
				data.tripConnection.edges = [
					newEdge,
					...data.tripConnection.edges.filter((e) => e.node.id !== newTrip.id),
				]

				// Increment total count if this is a new item
				if (!data.tripConnection.edges.some((e) => e.node.id === newTrip.id)) {
					data.tripConnection.totalCount = (data.tripConnection.totalCount || 0) + 1
				}
			}
			return data
		},
	)
}

// Function to remove a trip from the TripsByFilter query cache
export function removeTripFromFilterQueryCache(
	cache: Cache,
	tripId: string,
	filter: TripPaginationFilter,
) {
	const { sortedFields } = sortTripsCacheFields({ cache, filter })

	if (sortedFields.length === 0) {
		// If we're trying to remove a trip but there's no query for this filter,
		// we don't need to do anything
		return
	}

	sortedFields.forEach((field) => {
		// Clone the original arguments to avoid modifying them
		const originalArgs = field.arguments as any

		// Create a new variables object with the filter flattened
		const variables = {
			...originalArgs,
			filter: originalArgs.where?.filter, // Extract filter from where
		}

		cache.updateQuery<TripsByFilterQuery, TripsByFilterQueryVariables>(
			{
				query: TripsByFilterDocument,
				variables: variables as TripsByFilterQueryVariables,
			},
			(data) => {
				if (!data?.tripConnection?.edges) return data

				// Filter out the trip
				const originalLength = data.tripConnection.edges.length
				const updatedEdges = data.tripConnection.edges.filter((edge) => edge.node.id !== tripId)
				const removed = originalLength - updatedEdges.length

				return {
					...data,
					tripConnection: {
						...data.tripConnection,
						edges: updatedEdges,
						totalCount: Math.max(0, (data.tripConnection.totalCount || 0) - removed),
					},
				}
			},
		)
	})
}

export function deleteTripHandler(
	result: DeleteTripMutation,
	args: DeleteTripMutationVariables,
	cache: Cache,
) {
	const tripId = args.id

	// Update the TripsByFilter query for each filter type
	const filterTypes = [
		TripPaginationFilter.Planning,
		TripPaginationFilter.Published,
		TripPaginationFilter.Purchased,
	]

	filterTypes.forEach((filter) => {
		removeTripFromFilterQueryCache(cache, tripId, filter)
	})
}

export function ensureTripsConnection(data: TripsByFilterQuery | null): TripsByFilterQuery {
	const newData = data ? cloneDeep(data) : ({ __typename: 'Query' } as TripsByFilterQuery)

	if (newData.tripConnection == null) {
		newData.tripConnection = {
			edges: [],
			totalCount: 0,
			pageInfo: null,
			__typename: 'TripConnection',
		}
	}
	return newData
}

export function insertTripHandler(
	result: InsertTripMutation,
	args: InsertTripMutationVariables,
	cache: Cache,
) {
	// Insert the new trip into the Planning filter
	insertTripToFilterQueryCache(
		cache,
		result.insertTrip as unknown as TripsFieldsFragment,
		TripPaginationFilter.Planning,
	)
}

export function updateTripHandler(
	result: UpdateTripMutation,
	args: UpdateTripMutationVariables,
	cache: Cache,
) {
	// Update the trip details in the TripBase query
	cache.updateQuery<TripBaseQuery, TripBaseQueryVariables>(
		{ query: TripBaseDocument, variables: { tripId: args.id } },
		(data) => {
			if (data) {
				return {
					...data,
					title: result.updateTrip.title,
					description: result.updateTrip.description,
				}
			}
			return data
		},
	)

	// Update the trip in all filter types
	const filterTypes = [TripPaginationFilter.Planning, TripPaginationFilter.Published]

	filterTypes.forEach((filter) => {
		updateTripsByFilterQueryCache(
			cache,
			args.id,
			{
				title: result.updateTrip.title,
				description: result.updateTrip.description,
			},
			filter,
		)
	})
}

export function duplicateTripHandler(
	result: DuplicateTripMutation,
	args: DuplicateTripMutationVariables,
	cache: Cache,
) {
	// Insert the duplicated trip into the Planning filter
	insertTripToFilterQueryCache(
		cache,
		result.duplicateTrip as unknown as TripsFieldsFragment,
		TripPaginationFilter.Planning,
	)
}

export function makePurchaseHandler(
	result: MakePurchaseMutation,
	args: MakePurchaseMutationVariables,
	cache: Cache,
) {
	// Insert the purchased trip into the Purchased filter
	insertTripToFilterQueryCache(
		cache,
		result.makePurchase as unknown as TripsFieldsFragment,
		TripPaginationFilter.Purchased,
	)
}

export function publishListingHandler(
	result: PublishListingMutation,
	args: PublishListingMutationVariables,
	cache: Cache,
) {
	const tripId = result?.publishListing?.id

	// Remove the trip from Planning filter
	removeTripFromFilterQueryCache(cache, tripId, TripPaginationFilter.Planning)

	// Add the trip to Published filter
	insertTripToFilterQueryCache(
		cache,
		result.publishListing as unknown as TripsFieldsFragment,
		TripPaginationFilter.Published,
	)

	// Update the trip details
	cache.updateQuery<TripQuery, TripQueryVariables>(
		{ query: TripDocument, variables: { tripId } },
		(data) => {
			if (!data) return data

			return {
				...data,
				trip: {
					...data.trip,
					unpublishedListing: {
						...data.trip?.unpublishedListing,
						isPublished: true,
					},
				},
			}
		},
	)
}

export function unpublishListingHandler(
	result: UnpublishListingMutation,
	args: UnpublishListingMutationVariables,
	cache: Cache,
) {
	const tripId = result.unpublishListing.id

	// Remove the trip from Published filter
	removeTripFromFilterQueryCache(cache, tripId, TripPaginationFilter.Published)

	// Add the trip to Planning filter
	insertTripToFilterQueryCache(
		cache,
		result.unpublishListing as unknown as TripsFieldsFragment,
		TripPaginationFilter.Planning,
	)

	// Update the trip details
	cache.updateQuery<TripQuery, TripQueryVariables>(
		{ query: TripDocument, variables: { tripId } },
		(data) => {
			if (!data) return data

			return {
				...data,
				trip: {
					...data.trip,
					unpublishedListing: {
						...data.trip?.unpublishedListing,
						isPublished: false,
					},
				},
			}
		},
	)
}

export function insertMediaHandler(
	result: InsertMediaMutation,
	args: InsertMediaMutationVariables,
	cache: Cache,
) {
	const newMedia = result.insertMedia

	cache.updateQuery<MediaConnectionQuery, MediaConnectionQueryVariables>(
		{
			query: MediaConnectionDocument,
			variables: { tripId: args?.tripID, first: 10 },
		},
		(data) => {
			data = ensureMediaConnection(data)
			if (data.mediaConnection) {
				const newEdge = {
					cursor: null,
					__typename: 'MediaEdge',
					node: newMedia,
				}
				data.mediaConnection.edges = [
					// @ts-ignore
					newEdge,
					...data.mediaConnection.edges?.filter((e) => e.node.id !== newMedia.id).filter(Boolean),
				]
			}
			return data
		},
	)
}

export function ensureMediaConnection(data: MediaConnectionQuery | null): MediaConnectionQuery {
	const newData = data ? cloneDeep(data) : ({ __typename: 'Query' } as MediaConnectionQuery)
	if (newData.mediaConnection == null) {
		newData.mediaConnection = {
			edges: [],
			totalCount: 0,
			pageInfo: null,
			__typename: 'MediaConnection',
		}
	}
	return newData
}
